import { useState, useRef, useCallback, useEffect } from 'react'
import { MediaRecorder, IMediaRecorder } from 'extendable-media-recorder'

export type SpeakerLabel = 'clinician' | 'patient' | 'other'

interface SpeakerChange {
  speaker: SpeakerLabel
  timestampMs: number
  chunkIndex: number
}

interface UseEncounterAudioRecorderOptions {
  onChunk: (blob: Blob, chunkIndex: number, speaker: SpeakerLabel) => Promise<void>
  onError?: (error: Event) => void
  chunkIntervalMs?: number
}

export const useEncounterAudioRecorder = (options: UseEncounterAudioRecorderOptions) => {
  const { onChunk, onError, chunkIntervalMs = 10000 } = options

  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [currentSpeaker, setCurrentSpeaker] = useState<SpeakerLabel>('clinician')
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0)
  const [speakerChanges, setSpeakerChanges] = useState<SpeakerChange[]>([])

  const mediaRecorderRef = useRef<IMediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const chunkIndexRef = useRef(0)
  const currentSpeakerRef = useRef<SpeakerLabel>('clinician')
  const pendingChunksRef = useRef<Promise<void>[]>([])
  const onChunkRef = useRef(onChunk)
  onChunkRef.current = onChunk
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const startRecording = useCallback(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/wav' })
      mediaRecorderRef.current = recorder
      streamRef.current = stream
      chunkIndexRef.current = 0
      currentSpeakerRef.current = 'clinician'

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          const idx = chunkIndexRef.current
          const speaker = currentSpeakerRef.current
          chunkIndexRef.current = idx + 1
          setCurrentChunkIndex(idx + 1)
          const settled = { value: false }
          const chunkPromise = onChunkRef.current(e.data, idx, speaker).catch((err) => {
            console.error('Audio chunk processing failed:', err)
            onErrorRef.current?.(new ErrorEvent('chunk-error', { message: String(err) }))
          }).finally(() => { settled.value = true })
          ;(chunkPromise as any)._settled = settled
          pendingChunksRef.current.push(chunkPromise)
          // Prune settled promises every 50 chunks to prevent unbounded growth
          if (pendingChunksRef.current.length >= 50) {
            pendingChunksRef.current = pendingChunksRef.current.filter(
              p => !(p as any)._settled?.value
            )
          }
        }
      }

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
        setIsRecording(false)
        setIsPaused(false)
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        onErrorRef.current?.(event)
      }

      recorder.start(chunkIntervalMs)
      setIsRecording(true)
      setIsPaused(false)
      setRecordingTime(0)
      setCurrentSpeaker('clinician')
      setCurrentChunkIndex(0)
      setSpeakerChanges([])

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1)
      }, 1000)
    } catch (error) {
      console.error('Error starting encounter recording:', error)
      throw error
    }
  }, [chunkIntervalMs])

  const cleanup = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setIsRecording(false)
    setIsPaused(false)
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    // Note: cleanup is fire-and-forget; pending chunks are best-effort settled
    Promise.allSettled(pendingChunksRef.current).then(results => {
      const rejected = results.filter(r => r.status === 'rejected')
      if (rejected.length > 0) {
        console.warn(`${rejected.length} pending chunk(s) failed during cleanup`)
      }
      pendingChunksRef.current = []
    })
  }, [])

  const stopRecording = useCallback((): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const recorder = mediaRecorderRef.current
      if (!recorder || recorder.state === 'inactive') {
        console.warn('stopRecording called but recorder already inactive')
        Promise.allSettled(pendingChunksRef.current).then(() => {
          pendingChunksRef.current = []
          resolve()
        })
        return
      }

      const timeout = setTimeout(() => {
        reject(new Error('Stop recording timed out after 10 seconds'))
      }, 10000)

      recorder.onstop = async () => {
        clearTimeout(timeout)
        const results = await Promise.allSettled(pendingChunksRef.current)
        const failedOnStop = results.filter(r => r.status === 'rejected').length
        if (failedOnStop > 0) {
          console.warn(`${failedOnStop} audio chunk(s) failed during recording stop`)
        }
        pendingChunksRef.current = []
        resolve()
      }
      recorder.onerror = (event) => {
        clearTimeout(timeout)
        reject(event)
      }
      recorder.stop()
      setIsRecording(false)
      setIsPaused(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    })
  }, [])

  const togglePause = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume()
        setIsPaused(false)
        if (timerRef.current) clearInterval(timerRef.current)
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1)
        }, 1000)
      } else {
        mediaRecorderRef.current.pause()
        setIsPaused(true)
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }
  }, [isRecording, isPaused])

  const discardRecording = useCallback(() => {
    cleanup()
    setRecordingTime(0)
    setCurrentSpeaker('clinician')
    setCurrentChunkIndex(0)
    setSpeakerChanges([])
    chunkIndexRef.current = 0
    currentSpeakerRef.current = 'clinician'
  }, [cleanup])

  const markSpeaker = useCallback((speaker: SpeakerLabel) => {
    currentSpeakerRef.current = speaker
    setCurrentSpeaker(speaker)
    setSpeakerChanges(prev => [
      ...prev,
      {
        speaker,
        timestampMs: Date.now(),
        chunkIndex: chunkIndexRef.current,
      },
    ])
  }, [])

  // Cleanup timer, MediaRecorder, and MediaStream on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      mediaRecorderRef.current = null
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
  }, [])

  return {
    startRecording,
    stopRecording,
    togglePause,
    discardRecording,
    markSpeaker,
    isRecording,
    isPaused,
    recordingTime,
    currentSpeaker,
    currentChunkIndex,
    speakerChanges,
  }
}
